import { useState, useEffect, useCallback, useRef } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';
import { getProject, getProjectFiles, updateFile } from '../services/projects';
import { Project, ProjectFile } from '../types';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

const DEBOUNCE_MS = 1500;
const SAVED_FEEDBACK_MS = 2000;

export function useProject(projectId: string | undefined) {
  const [project, setProject] = useState<Project | null>(null);
  const [files, setFiles] = useState<ProjectFile[]>([]);
  const [activeFile, setActiveFile] = useState<ProjectFile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  // Whether the active file uses OT sync
  const isOTEnabled = activeFile?.otEnabled === true;

  // Refs for debounced auto-save
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedFeedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSaveRef = useRef<{ fileId: string; content: string } | null>(null);
  const activeFileIdRef = useRef<string | null>(null);
  // Track when the local user last edited (for non-OT real-time sync)
  const localEditTimestampRef = useRef<number>(0);

  // Keep activeFileIdRef in sync with activeFile
  useEffect(() => {
    activeFileIdRef.current = activeFile?.id ?? null;
  }, [activeFile?.id]);

  // Load project and files
  useEffect(() => {
    if (!projectId) return;
    const id = projectId;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [projectData, filesData] = await Promise.all([
          getProject(id),
          getProjectFiles(id),
        ]);

        if (!projectData) {
          setError('Project not found');
          return;
        }

        setProject(projectData);
        setFiles(filesData);

        const mainTex = filesData.find((f) => f.name === 'main.tex');
        const firstTex = filesData.find((f) => f.type === 'tex');
        setActiveFile(mainTex || firstTex || filesData[0] || null);
      } catch (err) {
        setError('Failed to load project');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [projectId]);

  // Execute the pending save to Firestore (legacy mode only)
  const executeSave = useCallback(async () => {
    const pending = pendingSaveRef.current;
    if (!pending || !projectId) return true;

    pendingSaveRef.current = null;
    setSaveStatus('saving');
    setSaveError(null);

    try {
      await updateFile(projectId, pending.fileId, { content: pending.content });
      setSaveStatus('saved');
      setLastSavedAt(new Date());
      if (savedFeedbackTimerRef.current) clearTimeout(savedFeedbackTimerRef.current);
      savedFeedbackTimerRef.current = setTimeout(() => setSaveStatus('idle'), SAVED_FEEDBACK_MS);
      return true;
    } catch (err) {
      console.error('Auto-save failed:', err);
      setSaveStatus('error');
      setSaveError('Failed to save');
      return false;
    }
  }, [projectId]);

  // Flush: cancel debounce and save immediately (legacy mode)
  const flushSave = useCallback(async () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    return executeSave();
  }, [executeSave]);

  // Legacy saveFile for direct file saves (used by non-active-file operations)
  const saveFile = useCallback(
    async (fileId: string, content: string) => {
      if (!projectId) return;
      await updateFile(projectId, fileId, { content });
      setFiles((prev) =>
        prev.map((f) => (f.id === fileId ? { ...f, content } : f))
      );
      if (activeFile?.id === fileId) {
        setActiveFile((prev) => (prev ? { ...prev, content } : null));
      }
    },
    [projectId, activeFile]
  );

  // Update content in memory + schedule debounced auto-save (legacy mode)
  // In OT mode, content updates come from the OT engine, not from debounced saves
  const updateActiveFileContent = useCallback((content: string, fromRemote = false) => {
    setActiveFile((prev) => (prev ? { ...prev, content } : null));

    // Also update files array (fixes stale-data bug where compile used outdated content)
    const fileId = activeFileIdRef.current;
    if (fileId) {
      setFiles((prev) => prev.map((f) => (f.id === fileId ? { ...f, content } : f)));

      // Track local edit timing (for non-OT real-time sync conflict avoidance)
      if (!fromRemote) {
        localEditTimestampRef.current = Date.now();
      }

      // Only schedule debounced save in legacy (non-OT) mode
      // In OT mode, the OT engine handles persistence via pendingOps → Cloud Function
      if (!isOTEnabled) {
        pendingSaveRef.current = { fileId, content };
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(() => {
          executeSave();
        }, DEBOUNCE_MS);
      }
    }
  }, [executeSave, isOTEnabled]);

  // Switch files safely: flush pending save first to prevent data loss
  const switchToFile = useCallback(async (file: ProjectFile) => {
    // In legacy mode, flush the debounced save
    if (!isOTEnabled) {
      await flushSave();
    }
    // Update ref synchronously BEFORE setActiveFile triggers re-render.
    // This prevents a race condition where Monaco's child effect fires
    // onChange (via model.setValue) before the useEffect that updates
    // activeFileIdRef runs, causing the new file's content to overwrite
    // the old file's entry in the files array.
    activeFileIdRef.current = file.id;
    // In OT mode, the engine handles synchronization —
    // the caller (EditorPage) should wait for isSynchronized before switching
    setActiveFile(file);
  }, [flushSave, isOTEnabled]);

  const refreshFiles = useCallback(async () => {
    if (!projectId) return;
    try {
      const filesData = await getProjectFiles(projectId);
      setFiles(filesData);

      const mainTex = filesData.find((f) => f.name === 'main.tex');
      const firstTex = filesData.find((f) => f.type === 'tex');
      setActiveFile(mainTex || firstTex || filesData[0] || null);
    } catch (err) {
      console.error('Failed to refresh files:', err);
    }
  }, [projectId]);

  // Real-time file sync for non-OT mode:
  // Listen to Firestore for content changes from other users.
  // In OT mode, the OT engine handles this via the operations subcollection.
  useEffect(() => {
    if (!projectId || !activeFile || isOTEnabled) return;

    const fileRef = doc(db, 'projects', projectId, 'files', activeFile.id);
    const unsub = onSnapshot(fileRef, (snapshot) => {
      const data = snapshot.data();
      if (!data || typeof data.content !== 'string') return;

      const remoteContent = data.content as string;

      // Skip if content matches local state (our own save echoing back)
      if (remoteContent === activeFile.content) return;

      // Skip if the user recently edited locally (within 3s) to avoid overwriting
      // their in-progress typing. Their debounced save will reconcile eventually.
      const timeSinceLocalEdit = Date.now() - localEditTimestampRef.current;
      if (timeSinceLocalEdit < 3000) return;

      // Apply the remote content update
      setActiveFile((prev) => prev ? { ...prev, content: remoteContent } : null);
      const fileId = activeFile.id;
      setFiles((prev) => prev.map((f) => f.id === fileId ? { ...f, content: remoteContent } : f));
    });

    return () => unsub();
    // Only re-subscribe when the file ID changes, not on every content update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, activeFile?.id, isOTEnabled]);

  // Cleanup: clear timers and attempt final save on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      if (savedFeedbackTimerRef.current) clearTimeout(savedFeedbackTimerRef.current);
      if (pendingSaveRef.current && projectId) {
        const { fileId, content } = pendingSaveRef.current;
        updateFile(projectId, fileId, { content }).catch(() => {});
      }
    };
  }, [projectId]);

  return {
    project,
    files,
    activeFile,
    setActiveFile,
    loading,
    error,
    saveFile,
    updateActiveFileContent,
    setFiles,
    refreshFiles,
    flushSave,
    switchToFile,
    saveStatus,
    saveError,
    lastSavedAt,
    isOTEnabled,
  };
}
